import type { Attributes, Span } from "@opentelemetry/api"
import { CART_UPDATED_AT_KEY } from "@/lib/cart-local-storage"
import type { SchedulingSlot } from "@/lib/scheduling/types"
import type { Address, Cart, CartItem, Service, Status } from "@/types"
import type { Discount, OrderRequest } from "@/types/external"
import type { OrderRequestDeliveryAddress } from "@/types/external/order-request"

/** Upper bound of one JSON attribute value; a larger payload is split into `key`, `key.1`, `key.2`… */
export const JSON_ATTR_LIMIT = 32_000

/** Most chunks one JSON payload may occupy (128 KB); keeps a batch of spans far below the 2 MiB ingress body limit. */
export const JSON_ATTR_MAX_CHUNKS = 4

export type CartOptionDetail = {
  groupId: string
  groupName: string
  groupStatus: Status
  priceMethod: string
  minPermitted: number
  maxPermitted: number
  optionId: string
  itemId: string
  name: string
  externalCode: string
  itemStatus?: Status
  optionStatus?: Status
  quantity: number
  price: number
  specialInstructions: string
}

export type CartLineDetail = {
  lineId: string
  itemId: string
  name: string
  externalCode: string
  itemStatus?: Status
  unit: string
  offerId: string
  offerStatus: Status
  offerIndex: number
  price: number
  originalPrice: number
  availabilityId: string[]
  isPromotional: boolean
  quantity: number
  unitPrice: number
  itemTotalPrice: number
  specialInstructions: string
  options: CartOptionDetail[]
}

export type CartDetail = {
  totalPrice: number
  lineCount: number
  itemCount: number
  lines: CartLineDetail[]
}

/** Every line of the bag with its frozen offer, prices and selected options; image and nutrition data left out. */
export function describeCart(cart: Cart | null | undefined): CartDetail {
  const items = cart?.items ?? []
  return {
    totalPrice: cart?.totalPrice ?? 0,
    lineCount: items.length,
    itemCount: items.reduce((sum, line) => sum + line.quantity, 0),
    lines: items.map(describeLine),
  }
}

function describeLine(line: CartItem): CartLineDetail {
  return {
    lineId: line.id,
    itemId: line.item.id,
    name: line.item.name,
    externalCode: line.item.externalCode ?? "",
    itemStatus: line.item.status,
    unit: line.item.unit ?? "",
    offerId: line.itemOffer.id,
    offerStatus: line.itemOffer.status,
    offerIndex: line.itemOffer.index,
    price: line.itemOffer.price.value,
    originalPrice: line.itemOffer.price.originalValue ?? line.itemOffer.price.value,
    availabilityId: line.itemOffer.availabilityId ?? [],
    isPromotional: line.itemOffer.isPromotional ?? false,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    itemTotalPrice: line.itemTotalPrice,
    specialInstructions: line.specialInstructions ?? "",
    options: describeOptions(line),
  }
}

function describeOptions(line: CartItem): CartOptionDetail[] {
  const notes = line.optionSpecialInstructions ?? {}
  return Object.entries(line.optionQuantities ?? {}).flatMap(([groupId, quantities]) => {
    const group = line.optionGroups.find((candidate) => candidate.id === groupId)
    return Object.entries(quantities ?? {})
      .filter(([, quantity]) => quantity > 0)
      .map(([optionId, quantity]) => {
        const option = group?.options.find((candidate) => candidate.id === optionId)
        const item = line.itemOptions?.[optionId]
        return {
          groupId,
          groupName: group?.name ?? "",
          groupStatus: group?.status ?? "AVAILABLE",
          priceMethod: group?.priceMethod ?? "",
          minPermitted: group?.minPermitted ?? 0,
          maxPermitted: group?.maxPermitted ?? 0,
          optionId,
          itemId: item?.id ?? option?.itemId ?? "",
          name: item?.name ?? "",
          externalCode: item?.externalCode ?? "",
          itemStatus: item?.status,
          optionStatus: option?.status,
          quantity,
          price: option?.price.value ?? 0,
          specialInstructions: notes[groupId]?.[optionId] ?? "",
        }
      })
  })
}

/** The POST body as sent, minus the customer's street address, coordinates and payment transaction data. */
export function describeOrderRequest(request: OrderRequest): Record<string, unknown> {
  const { delivery, payments, ...rest } = request
  return {
    ...rest,
    payments: {
      ...payments,
      methods: payments.methods.map(({ transaction: _transaction, ...method }) => method),
    },
    ...(delivery
      ? {
          delivery: {
            ...delivery,
            deliveryAddress: redactAddress(delivery.deliveryAddress),
          },
        }
      : {}),
  }
}

function redactAddress(address: OrderRequestDeliveryAddress): Record<string, unknown> {
  return {
    country: address.country,
    state: address.state,
    city: address.city,
    district: address.district,
    postalCode: address.postalCode,
  }
}

/** A channel's hours and timings; the delivery polygon is left out. */
export function describeService(service: Service | null): Record<string, unknown> | null {
  if (!service) return null
  return {
    id: service.id,
    menuId: service.menuId,
    serviceType: service.serviceType,
    status: service.status,
    serviceHours: service.serviceHours,
    serviceTiming: service.serviceTiming,
  }
}

export type CheckoutContext = {
  isTableOrder: boolean
  tableId?: string
  tableExternalCode?: string
  merchantStatus?: string | null
  minOrderValue?: number
  activeOrders: number
  couponDiscounts?: Discount[]
  loyaltyPoints: number
  benefitDiscounts?: Discount[]
  servicePrice: number
  pixServiceFee: number
  changeFor: number
  paymentMethodId?: string
  methodInfo?: string
  specialInstructions: string
  scheduledSlot: SchedulingSlot | null
  scheduledArrival: { start: string; end: string } | null
  address?: Address
}

/** Scalar checkout inputs as span attributes; the address keeps only city, district and postal code. */
export function checkoutContextAttributes(ctx: CheckoutContext): Attributes {
  const attrs: Attributes = {
    "checkout.is_table_order": ctx.isTableOrder,
    "checkout.active_orders": ctx.activeOrders,
    "checkout.coupon_price": (ctx.couponDiscounts ?? []).reduce((sum, discount) => sum + discount.amount.value, 0),
    "checkout.loyalty_points": ctx.loyaltyPoints,
    "checkout.service_price": ctx.servicePrice,
    "checkout.pix_service_fee": ctx.pixServiceFee,
    "checkout.change_for": ctx.changeFor,
    "checkout.special_instructions": ctx.specialInstructions.slice(0, 1000),
  }
  if (ctx.tableId) attrs["checkout.table_id"] = ctx.tableId
  if (ctx.tableExternalCode) attrs["checkout.table_external_code"] = ctx.tableExternalCode
  if (ctx.merchantStatus) attrs["checkout.merchant_status"] = ctx.merchantStatus
  if (typeof ctx.minOrderValue === "number") attrs["checkout.min_order_value"] = ctx.minOrderValue
  const couponCodes = [
    ...new Set((ctx.couponDiscounts ?? []).flatMap((discount) => discount.sponsorshipValues.map((s) => s.discountCode))),
  ]
  if (couponCodes.length > 0) attrs["checkout.coupon_code"] = couponCodes.join(",")
  if (ctx.benefitDiscounts?.length) {
    attrs["checkout.benefit_discounts"] = jsonAttr(ctx.benefitDiscounts, 4000)
  }
  if (ctx.paymentMethodId) attrs["checkout.payment_method_id"] = ctx.paymentMethodId
  if (ctx.methodInfo) attrs["checkout.method_info"] = ctx.methodInfo
  if (ctx.scheduledSlot) attrs["checkout.scheduled_slot"] = jsonAttr(ctx.scheduledSlot, 2000)
  if (ctx.scheduledArrival) {
    attrs["checkout.scheduled_arrival_start"] = ctx.scheduledArrival.start
    attrs["checkout.scheduled_arrival_end"] = ctx.scheduledArrival.end
  }
  if (ctx.address) {
    attrs["checkout.address_id"] = ctx.address.id
    attrs["checkout.address_city"] = ctx.address.city
    attrs["checkout.address_state"] = ctx.address.state
    attrs["checkout.address_district"] = ctx.address.district
    attrs["checkout.address_postal_code"] = ctx.address.postalCode
  }
  const updatedAt = cartUpdatedAtIso()
  if (updatedAt) attrs["checkout.cart_updated_at"] = updatedAt
  return attrs
}

/** When the bag was last touched, from the TTL anchor; null when unknown. */
export function cartUpdatedAtIso(): string | null {
  try {
    const raw = localStorage.getItem(CART_UPDATED_AT_KEY)
    const ms = raw === null ? NaN : Number(raw)
    return Number.isNaN(ms) ? null : new Date(ms).toISOString()
  } catch {
    return null
  }
}

/** Compact JSON for an attribute; cut at `limit` chars with an explicit marker so a partial payload is never mistaken for a whole one. */
export function jsonAttr(value: unknown, limit = JSON_ATTR_LIMIT): string {
  let serialized: string
  try {
    serialized = JSON.stringify(value) ?? ""
  } catch {
    serialized = String(value)
  }
  if (serialized.length <= limit) return serialized
  return `${serialized.slice(0, limit)}…[truncated ${serialized.length - limit} chars]`
}

/**
 * Sets `key` to the JSON of `value`, spilling into `key.1`, `key.2`… when it
 * exceeds `limit` (`<key>_chunks` says how many to concatenate). Past
 * `JSON_ATTR_MAX_CHUNKS` the tail is cut and `<key>_truncated` is set.
 */
export function setJsonAttribute(span: Span, key: string, value: unknown, limit = JSON_ATTR_LIMIT): void {
  const budget = limit * JSON_ATTR_MAX_CHUNKS
  const serialized = jsonAttr(value, budget - TRUNCATION_MARKER_RESERVE)
  const chunks = Math.max(1, Math.ceil(serialized.length / limit))
  for (let index = 0; index < chunks; index += 1) {
    span.setAttribute(index === 0 ? key : `${key}.${index}`, serialized.slice(index * limit, (index + 1) * limit))
  }
  if (chunks > 1) span.setAttribute(`${key}_chunks`, chunks)
  if (serialized.length > budget - TRUNCATION_MARKER_RESERVE) span.setAttribute(`${key}_truncated`, true)
}

const TRUNCATION_MARKER_RESERVE = 40
